import moment from "moment";

const phone = {
    namespaced: true,
    state: {
        sipConfiguration: null,
        phonebook: {},
        reversePhonebook: {},
        phoneState: {
            action: null, // start_call, end_call, mute, unmute
            recipient: '',
            isGroupCall: false,
            isMuted: false,
            status: 'disconnected', // unconfigured, connected, disconnected, registered, unregistered, group_call_setup, calling, call_connected, call_ended, call_error
            callStartTime: null
        }
    },
    mutations: {
        // Update current data from vTraveller.
        updateSipConfiguration(state, configuration) {
            state.sipConfiguration = configuration;
        },
        updatePhonebook(state, phonebook) {
            // The field "callStatus" which is updated via events should not be overwritten by a periodic phonebook update.
            let newPhonebook = {};
            for (const [key, value] of Object.entries(phonebook)) {
                newPhonebook[key] = { ...state.phonebook[key], ...value };
            }
            state.phonebook = newPhonebook;
        },
        // Data from Traveller.
        createReversePhonebook(state, vehicleList) {
            let reversePB = {};
            for (let vehicle of vehicleList) {
                if (vehicle.vehicleLabel && vehicle.features && vehicle.features.SIP_CLIENT_ID) {
                    reversePB[vehicle.features.SIP_CLIENT_ID] = vehicle.vehicleLabel;
                }
            }
            state.reversePhonebook = reversePB;
        },
        // Requests for action.
        requestIndividualCall(state, recipient) {
            if (state.phoneState.status != 'unconfigured' &&
                state.phoneState.status != 'disconnected' &&
                state.phoneState.status != 'unregistered') {
                state.phoneState = { ...state.phoneState, action: 'start_call', status: 'calling', recipient: recipient, isGroupCall: false, isMuted: false };
            }
        },
        requestGroupCall(state, newRecipient) {
            if (state.phoneState.status != 'unconfigured' &&
                state.phoneState.status != 'disconnected' &&
                state.phoneState.status != 'unregistered') {
                let recipient = state.phoneState.recipient;
                if (newRecipient) {
                    recipient = newRecipient;
                }
                state.phoneState = { ...state.phoneState, action: 'start_call', status: 'calling', recipient: recipient, isGroupCall: true, isMuted: false };
            }
        },
        requestEndCall(state) {
            state.phoneState = { ...state.phoneState, action: 'end_call' };
        },
        requestMute(state) {
            state.phoneState = { ...state.phoneState, action: 'mute' };
        },
        requestUnmute(state) {
            state.phoneState = { ...state.phoneState, action: 'unmute' };
        },
        // Changes in the phone state.
        unconfigured(state) {
            state.phoneState = { ...state.phoneState, action: null, status: 'unconfigured', isGroupCall: false };
        },
        connected(state) {
            state.phoneState = { ...state.phoneState, action: null, status: 'connected', isGroupCall: false };
        },
        disconnected(state) {
            state.phoneState = { ...state.phoneState, action: null, status: 'disconnected', isGroupCall: false };
        },
        registered(state) {
            // Don't let a periodic reregistration to change the UI when a call is in view.
            if (state.phoneState.status != 'group_call_setup' && state.phoneState.status != 'call_ended') {
                state.phoneState = { ...state.phoneState, action: null, status: 'registered', isGroupCall: false };
            }
        },
        unregistered(state) {
            state.phoneState = { ...state.phoneState, action: null, status: 'unregistered', isGroupCall: false };
        },
        callConnected(state) {
            state.phoneState = { ...state.phoneState, action: null, status: 'call_connected', callStartTime: new moment() };
        },
        callEnded(state) {
            state.phoneState = { ...state.phoneState, action: null, status: 'call_ended' };
        },
        callError(state) {
            state.phoneState = { ...state.phoneState, action: null, status: 'call_error' };
        },
        muted(state) {
            state.phoneState = { ...state.phoneState, action: null, isMuted: true };
        },
        unmuted(state) {
            state.phoneState = { ...state.phoneState, action: null, isMuted: false };
        },
        addGroupCallRecipient(state, sipClientId) {
            if (state.phoneState.status != 'unconfigured' &&
                state.phoneState.status != 'disconnected' &&
                state.phoneState.status != 'unregistered') {
                let recipients = state.phoneState.recipient;
                if (!Array.isArray(recipients))
                    recipients = [];
                if (!recipients.find(recipient => recipient == sipClientId)) {
                    recipients.push(sipClientId);
                }
                state.phoneState = { ...state.phoneState, status: 'group_call_setup', recipient: recipients, isGroupCall: true };
            }
        }, 
        removeGroupCallRecipient(state, sipClientId) {
            let recipients = state.phoneState.recipient;
            if (!Array.isArray(recipients)) {
                recipients = [];
            }
            recipients = recipients.filter((item) => {
                return item != sipClientId;
            });
            state.phoneState = { ...state.phoneState, status: 'group_call_setup', recipient: recipients, isGroupCall: true };
        },
        removeAllGroupCallRecipients(state) {
            state.phoneState = { ...state.phoneState, status: 'group_call_setup', recipient: '', isGroupCall: true };
        }
    },
    actions: {
        getSipConfiguration(context, sipClientId) {
            return new Promise((resolve, reject) => {
                this._vm.$vtraveller_http.get('/vtraveller/config/sipClient', { params: { "sipClientId": sipClientId }})
                    .then(response => {
                        if (response.status == 200) {
                            context.commit('updateSipConfiguration', response.data.data);
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        },
        getPhonebook(context) {
            return new Promise((resolve, reject) => {
                this._vm.$vtraveller_http.get('/vtraveller/config/phonebook')
                    .then(response => {
                        if (response.status == 200 && Array.isArray(response.data.data)) {
                            let phonebook = {};
                            for (let entry of response.data.data) {
                                if (entry.sipClientId) {
                                    phonebook[entry.sipClientId] = entry;
                                }
                            }
                            context.commit('updatePhonebook', phonebook);
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        },
        makeIndividualCall(context) {
            return new Promise((resolve, reject) => {
                // Make sure that UI components can set the recipient to be either an array or not, just in case it's needed.
                let recipient;
                if (Array.isArray(context.state.phoneState.recipient)) {
                    recipient = context.state.phoneState.recipient[0];
                } else {
                    recipient = context.state.phoneState.recipient;
                }
                if (!context.state.phonebook[recipient]) {
                    context.commit('callError');
                    reject();
                }
                let callParameters = {
                    callingParty: context.state.sipConfiguration.sipExtensionType +'/'+ context.state.sipConfiguration.sipExtension,
                    calledParty: context.state.phonebook[recipient].sipExtensionType +'/'+ 
                        context.state.phonebook[recipient].sipExtension
                };
                this._vm.$vtraveller_http.post('/vtraveller/switch/makeIndividualCall', callParameters)
                    .then(response => {
                        resolveCall(response, resolve, context, reject);
                    })
                    .catch(error => {
                        context.commit('callError');
                        reject(error);
                    });
            });
        },
        makeGroupCall(context) {
            context.dispatch("subscribeToEvents", context.state.sipConfiguration.sipClientId)
            return new Promise((resolve, reject) => {
                let recipient;
                if (Array.isArray(context.state.phoneState.recipient)) {
                    recipient = context.state.phoneState.recipient;
                } else {
                    recipient = [context.state.phoneState.recipient];
                }
                let list = [];
                for (const r of recipient) {
                    if (context.state.phonebook[r]) {
                        list.push(context.state.phonebook[r].sipExtensionType +'/'+ context.state.phonebook[r].sipExtension);
                    }
                    // The call will go on even if there is a vehicle with a sipId that isn't found in the phonebook.
                    // This vehicle will be considered offline for the duration of the call.
                }
                let callParameters = {
                    callingParty: context.state.sipConfiguration.sipExtensionType +'/'+ context.state.sipConfiguration.sipExtension,
                    calledParties: list
                };
                this._vm.$vtraveller_http.post('/vtraveller/switch/makeGroupCall', callParameters)
                    .then(response => {
                        resolveCall(response, resolve, context, reject);
                    })
                    .catch(error => {
                        context.commit('callError');
                        reject(error);
                    });
            });
        },
        subscribeToEvents( _context, sipClientId ) {
            let subscriptionParameters = {
                sipClientId: sipClientId
            };
            return vtPostPromise.call(this, '/vtraveller/switch/subscribeToEvents', subscriptionParameters);
        },
        unsubscribeFromEvents(_context, sipClientId ) {
            let unsubscriptionParameters = {
                sipClientId: sipClientId
            };
            return vtPostPromise.call(this, '/vtraveller/switch/unsubscribeFromEvents', unsubscriptionParameters);
        },
        channelStateChange( context , payload ) {
            let newPhonebook = JSON.parse(JSON.stringify(context.state.phonebook));
            let sipClient = newPhonebook[payload.sipClientId]
            if( sipClient ) {
                if( payload.state == 'up' || payload.state == 'hangup' ) {
                    sipClient.status = 'online'
                    sipClient.callStatus = payload.state
                } else if( payload.state == 'offline' ) {
                    sipClient.status = 'offline'
                    sipClient.callStatus = ''
                } else if( payload.state == 'ringing' ) {
                    sipClient.status = 'online'
                    sipClient.callStatus = 'ringing'
                }
            } else {
                //console.log("Phone not found with ip " + payload.sipClientId )
            }
            context.commit('updatePhonebook' , newPhonebook )
        }
    }
}
export default phone;

function resolveCall(response, resolve, context, reject) {
    if (response.status == 200) {
        if (response.data.code == 0) {
            resolve(response);
        } else {
            context.commit('callError');
            reject();
        }
    } else {
        context.commit('callError');
        reject();
    }
}

function vtPostPromise( url, parameters ) {
    return new Promise((resolve,reject) => {
        this._vm.$vtraveller_http.post(url, parameters)
            .then(response => {
                if (response.status == 200) {
                    if (response.data.code == 0) {
                        resolve(response);
                    } else {
                        reject();
                    }
                }
            })
            .catch(error => {
                reject(error);
            });
    })
}

